'use client';

/**
 * useUnderlyingAmounts Hook
 *
 * Calculates the underlying token amounts for LP positions.
 * For a user holding LP tokens, this computes how much of each
 * underlying token they would receive if they withdrew.
 *
 * Formula:
 * userTokenAmount = (userLpBalance / totalLpSupply) * reserveAmount
 */

import { useMemo } from 'react';
import { useReadContracts } from 'wagmi';
import { formatUnits, type Address } from 'viem';
import type { UnderlyingAsset } from '@/lib/masterchef-cache/types';

// Minimal ABI for LP token queries
const LP_PAIR_ABI = [
  {
    type: 'function',
    name: 'totalSupply',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    name: 'getReserves',
    inputs: [],
    outputs: [
      { name: '_reserve0', type: 'uint112' },
      { name: '_reserve1', type: 'uint112' },
      { name: '_blockTimestampLast', type: 'uint32' },
    ],
    stateMutability: 'view',
  },
  {
    type: 'function',
    name: 'token0',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
  },
  {
    type: 'function',
    name: 'token1',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
    stateMutability: 'view',
  },
] as const;

export interface UnderlyingAmount {
  asset: UnderlyingAsset;
  amount: bigint;
  amountFormatted: string;
}

export interface UseUnderlyingAmountsOptions {
  lpTokenAddress: Address;
  userLpBalance: bigint;
  underlyingAssets: UnderlyingAsset[];
  chainId: number;
  enabled?: boolean;
}

export interface UseUnderlyingAmountsReturn {
  amounts: UnderlyingAmount[];
  isLoading: boolean;
  error: Error | null;
  totalSupply: bigint | null;
  reserves: [bigint, bigint] | null;
}

export function useUnderlyingAmounts({
  lpTokenAddress,
  userLpBalance,
  underlyingAssets,
  chainId,
  enabled = true,
}: UseUnderlyingAmountsOptions): UseUnderlyingAmountsReturn {
  // For single-asset staking (not LP pairs), return the staked amount directly
  const isSingleAsset = underlyingAssets.length === 1;

  // Fetch LP pair data (totalSupply, reserves, token order)
  const { data, isLoading, error } = useReadContracts({
    contracts: [
      {
        address: lpTokenAddress,
        abi: LP_PAIR_ABI,
        functionName: 'totalSupply',
        chainId,
      },
      {
        address: lpTokenAddress,
        abi: LP_PAIR_ABI,
        functionName: 'getReserves',
        chainId,
      },
      {
        address: lpTokenAddress,
        abi: LP_PAIR_ABI,
        functionName: 'token0',
        chainId,
      },
      {
        address: lpTokenAddress,
        abi: LP_PAIR_ABI,
        functionName: 'token1',
        chainId,
      },
    ],
    query: {
      enabled: enabled && !isSingleAsset && underlyingAssets.length === 2,
    },
  });

  // Calculate underlying amounts
  const result = useMemo((): {
    amounts: UnderlyingAmount[];
    totalSupply: bigint | null;
    reserves: [bigint, bigint] | null;
  } => {
    // For single asset staking, return the staked amount directly
    if (isSingleAsset && underlyingAssets.length === 1) {
      const asset = underlyingAssets[0];
      return {
        amounts: [
          {
            asset,
            amount: userLpBalance,
            amountFormatted: formatUnits(userLpBalance, asset.decimals),
          },
        ],
        totalSupply: null,
        reserves: null,
      };
    }

    // Need LP pair data for LP tokens
    if (!data || underlyingAssets.length !== 2) {
      return {
        amounts: [],
        totalSupply: null,
        reserves: null,
      };
    }

    const [totalSupplyResult, reservesResult, token0Result, token1Result] = data;

    // Check all calls succeeded
    if (
      totalSupplyResult.status !== 'success' ||
      reservesResult.status !== 'success' ||
      token0Result.status !== 'success' ||
      token1Result.status !== 'success'
    ) {
      return {
        amounts: [],
        totalSupply: null,
        reserves: null,
      };
    }

    const totalSupply = totalSupplyResult.result as bigint;
    const reserves = reservesResult.result as [bigint, bigint, number];
    const token0Address = (token0Result.result as string).toLowerCase();
    const token1Address = (token1Result.result as string).toLowerCase();

    // Can't divide by zero
    if (totalSupply === 0n) {
      return {
        amounts: [],
        totalSupply,
        reserves: [reserves[0], reserves[1]],
      };
    }

    // Match underlying assets to token0/token1 order
    const asset0 = underlyingAssets.find(
      (a) => a.address.toLowerCase() === token0Address
    );
    const asset1 = underlyingAssets.find(
      (a) => a.address.toLowerCase() === token1Address
    );

    if (!asset0 || !asset1) {
      // Fallback: use the order provided
      const [firstAsset, secondAsset] = underlyingAssets;
      const amount0 = (userLpBalance * reserves[0]) / totalSupply;
      const amount1 = (userLpBalance * reserves[1]) / totalSupply;

      return {
        amounts: [
          {
            asset: firstAsset,
            amount: amount0,
            amountFormatted: formatUnits(amount0, firstAsset.decimals),
          },
          {
            asset: secondAsset,
            amount: amount1,
            amountFormatted: formatUnits(amount1, secondAsset.decimals),
          },
        ],
        totalSupply,
        reserves: [reserves[0], reserves[1]],
      };
    }

    // Calculate user's share of each reserve
    const amount0 = (userLpBalance * reserves[0]) / totalSupply;
    const amount1 = (userLpBalance * reserves[1]) / totalSupply;

    return {
      amounts: [
        {
          asset: asset0,
          amount: amount0,
          amountFormatted: formatUnits(amount0, asset0.decimals),
        },
        {
          asset: asset1,
          amount: amount1,
          amountFormatted: formatUnits(amount1, asset1.decimals),
        },
      ],
      totalSupply,
      reserves: [reserves[0], reserves[1]],
    };
  }, [data, userLpBalance, underlyingAssets, isSingleAsset]);

  return {
    amounts: result.amounts,
    isLoading: isSingleAsset ? false : isLoading,
    error: error || null,
    totalSupply: result.totalSupply,
    reserves: result.reserves,
  };
}
